import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { CircularProgress, Grid } from '@material-ui/core';

import { useScript } from 'hooks';

import TimeCode from 'commons/utils/TimeCode';
import { SearchCondition } from 'commons/constants/KeyFlowConstants';
import ScriptParamVO from 'commons/vo/param/ScriptParamVO';

import { SCRIPT_HEIGHT } from 'components/Script/useStyles';
import { Script, useStyles } from 'components/Script';
import { ScrollBarsAnimate } from 'components/Scroll';

import _ from 'lodash';
import ScriptImpl from 'commons/vo/Script';

function ScriptList(
  {
    autoScrollMode,
    onChangeCurrentTime,
    onChangeInTime,
    onChangeOutTime,
    onChangePartialTitle,
    team1PlayerList,
    team2PlayerList,
  },
  ref,
) {
  const playerIdMap = useMemo(() => {
    const map = {};
    const lists = [team1PlayerList, team2PlayerList];
    lists.forEach(playerList => {
      if (playerList && Array.isArray(playerList)) {
        playerList.forEach(player => {
          // key는 `${number}_${name}`
          if (player && player.number && player.name) {
            map[`${player.number}_${player.name}`] = player.personId;
          }
        });
      }
    });
    return map;
  }, [team1PlayerList, team2PlayerList]);

  const scriptListRef = useRef();
  const loadingRef = useRef();

  const classes = useStyles();
  const { scriptList, isLoading, isLoadingByWheel, pagination, keyword } = useSelector(
    state => state.script,
  );

  const { selectedTeam1, selectedTeam2 } = useSelector(state => state.asset);
  const { selectedAsset, selectedQuarter } = useSelector(state => state.asset);

  const { onRequestScriptListByScroll } = useScript();

  const [isWheelLoading, setIsWheelLoading] = useState(false);

  const { mediaFps: fps, symbolicPath } = useMemo(() => {
    let result = {
      mediaFps: 29.97,
      symbolicPath: '',
    };
    if (selectedAsset) {
      result.mediaFps = selectedAsset.mediaFps;
      result.symbolicPath = selectedAsset.symbolicPath;
    }
    return result;
  }, [selectedAsset]);

  const timecodeUtil = useMemo(() => {
    const timecode = new TimeCode(fps);
    return timecode;
  }, [fps]);

  useImperativeHandle(
    ref,
    () => ({
      scrollTop: currentTime => {
        if (autoScrollMode) {
          const scriptHeight = SCRIPT_HEIGHT;
          const { length } = scriptList;
          const index = scriptList.findIndex(script => {
            timecodeUtil.setFrames(script.inPoint);
            const inTime = timecodeUtil.getSeconds();
            return inTime >= Math.floor(currentTime);
          });

          if (index !== -1) {
            let percent = index / length;
            percent < 0 && (percent = 0);
            scriptListRef.current.scrollTop((index - 1) * scriptHeight);
          }
        }
      },
    }),
    [timecodeUtil, autoScrollMode, scriptList, scriptListRef.current],
  );

  useEffect(() => {
    if (!isLoadingByWheel) {
      setIsWheelLoading(false);
    }
  }, [isLoadingByWheel]);

  // useEffect(() => {
  //   if (isLoading) {
  //     scriptListRef.current.scrollTop(0);
  //   }
  // }, [isLoading]);
  const onWheel = useCallback(() => {
    if (!scriptListRef.current || !loadingRef.current) {
      return;
    }
    const scrollHeight = scriptListRef.current.getScrollHeight(); // 전체 스크롤 높이
    const scrollTop = scriptListRef.current.getScrollTop(); // 현재 스크롤 위치
    const height = scriptListRef.current.getClientHeight(); // 창 높이
    const scriptHeight = SCRIPT_HEIGHT;
    const pageNumber = pagination.pageNumber;
    const pageCount = pagination.pageCount;

    if (pageNumber === pageCount) {
      return;
    }

    if (isWheelLoading) {
      return;
    }
    if (scrollHeight - loadingRef.current.offsetHeight / 2 <= scrollTop + height + scriptHeight) {
      setIsWheelLoading(true);

      const scriptParam = new ScriptParamVO({
        retr: 100,
        page: pageNumber + 1,
        sort: [SearchCondition.Sort.IN_POINT, SearchCondition.Sort.OUT_POINT],
        asc: SearchCondition.Asc.ASC,
        videoId: selectedAsset.id,
        keyword,
        inPoint: selectedQuarter ? selectedQuarter.inPoint : undefined,
        outPoint: selectedQuarter ? selectedQuarter.outPoint : undefined,
      });
      onRequestScriptListByScroll(scriptParam);
    }
  }, [scriptListRef.current, selectedAsset, selectedQuarter, pagination, isWheelLoading, keyword]);

  const getPlayerIdFromScript = useCallback(
    script => {
      const text = script;
      const matchedPlayer = [];
      [...team1PlayerList, ...team2PlayerList].forEach(player => {
        const newName = text.includes(player.name);
        const newNumber = text.includes(player.number);

        if (newName && newNumber) {
          const indexInText = text.indexOf(player.name);
          matchedPlayer.push({ ...player, indexInText });
        }
      });

      if (text.includes('교체') && matchedPlayer.length === 2) {
        const copyMatchedPlayer = matchedPlayer.slice();
        const sortedMatchedPlayer = copyMatchedPlayer.sort((a, b) => a.indexInText - b.indexInText);
        // 교체 스크립트에서 교체(IN)되는 선수 추출
        const secondPlayer = sortedMatchedPlayer[1];
        if (secondPlayer) {
          return `${secondPlayer.number}_${secondPlayer.name}`;
        }
      }

      if (matchedPlayer && matchedPlayer.length > 0) {
        const lastIndex = matchedPlayer.length - 1;
        const word = matchedPlayer[lastIndex].number + '_' + matchedPlayer[lastIndex].name;
        return word;
      }
    },
    [playerIdMap],
  );

  // 팀 이름 다음에 숫자가 있는지 판별
  const checkTeamNameFollowedByNumber = useCallback((teamName, script) => {
    if (!teamName || !script) {
      return false;
    }
    const teamIndex = script.indexOf(teamName);

    if (teamIndex === -1) {
      return false;
    }

    const afterTeamName = script.substring(teamIndex + teamName.length).trim();

    return /^\d/.test(afterTeamName);
  }, []);

  return (
    <div className={classes.root}>
      <ScrollBarsAnimate ref={scriptListRef} onWheel={onWheel} onTouchEnd={onWheel}>
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              color: '#888',
              padding: '32px 0',
            }}
          >
            <CircularProgress className={classes.circularProgress} />
          </div>
        )}
        {!isLoading && scriptList.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '32px 0' }}>
            데이터가 존재하지 않습니다.
          </div>
        ) : (
          !isLoading &&
          scriptList.map(script => {
            // TODO
            /**
             * 배포할때 확인할 것(기존 서비스에서는 에러나지 않는 부분)
             * script.fps = fps;
             **/
            const scriptData = {
              ID: script.id,
              CATEGORY_ID: script.categoryId,
              SCRIPT: script.script,
              OWNER: script.owner,
              VIDEO_ID: script.videoId,
              IN_POINT: script.inPoint,
              OUT_POINT: script.outPoint,
              THUMBNAIL_PATH: script.thumbnailPath,
              THUMBNAIL_FRAME: script.thumbnailFrame,
              DOWNLOAD_COUNT: script.downloadCount,
              RATING: script.rating,
              DATE_OF_CREATED: script.dateOfCreated,
              DATE_OF_MODIFIED: script.dateOfModified,
              QUARTER: script.quarter,
              GAME_TIME: script.gameTime,
              SCORE: script.score,
              TYPE: script.type,
            };

            const newScript = new ScriptImpl(scriptData);
            newScript.fps = fps;

            const personId = playerIdMap[getPlayerIdFromScript(script.script)];

            const team1Id =
              script.script.includes(selectedTeam1?.name) &&
              !checkTeamNameFollowedByNumber(selectedTeam1?.name, script.script)
                ? selectedTeam1?.id
                : undefined;

            const team2Id =
              script.script.includes(selectedTeam2?.name) &&
              !checkTeamNameFollowedByNumber(selectedTeam2?.name, script.script)
                ? selectedTeam2?.id
                : undefined;
            return (
              <Script
                key={script.id}
                script={newScript}
                isTeam={team1Id || team2Id}
                id={personId || team1Id || team2Id}
                onChangeCurrentTime={onChangeCurrentTime}
                onChangeInTime={onChangeInTime}
                onChangeOutTime={onChangeOutTime}
                onChangePartialTitle={onChangePartialTitle}
              />
            );
          })
        )}
        <Grid
          ref={loadingRef}
          container
          justifyContent="center"
          className={clsx(classes.scriptLoadingContainer, !isLoadingByWheel && classes.displayNone)}
        >
          <Grid item>
            <CircularProgress className={classes.circularProgress} />
          </Grid>
        </Grid>
      </ScrollBarsAnimate>
    </div>
  );
}

export default forwardRef(ScriptList);
